import { Typography, Slider } from '@mui/material';
import { SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import { VideoTimeLine } from './VideoPanel/VideoTimeLine';

type VideoThumbnailSelectProps = {
  media: File;
  onChange: (timeInSeconds: number) => void;
  width: number;
  height: number;
  ratio: number;
  startTime?: number;
  endTime?: number;
};

const getDuration = (
  props: Pick<VideoThumbnailSelectProps, 'startTime' | 'endTime'> & {
    duration: number;
  },
): number => {
  if (props.startTime && props.endTime) {
    return props.endTime - props.startTime;
  }

  if (props.startTime) {
    return props.duration - props.startTime;
  }

  if (props.endTime) {
    return props.endTime;
  }

  return props.duration;
};

function MediaThumbnailSelect(props: VideoThumbnailSelectProps) {
  const videoSrc = useMemo(
    () => URL.createObjectURL(props.media),
    [props.media],
  );
  const playerRef = useRef<ReactPlayer>(null);
  const [metadata, setMetadata] = useState({ duration: 0 });
  const [sliderValue, setSliderValue] = useState(props.startTime || 0);

  useEffect(() => {
    if (props.startTime && playerRef?.current) {
      playerRef.current.seekTo(props.startTime);
    }
  }, [playerRef.current]);

  const handleSliderChange = (
    _event: Event | null,
    newValue: number | number[],
  ) => {
    const value = newValue as number;
    setSliderValue(value);
    if (playerRef?.current) {
      playerRef.current.seekTo(value);
    }
  };

  const saveTimeStamp = (
    event: Event | SyntheticEvent<Element, Event> | null,
    newValue: number | number[],
  ) => {
    props.onChange(newValue as number);
  };

  const thumbnailClick = (timestamp: number) => {
    setSliderValue(timestamp);
    if (playerRef?.current) {
      playerRef.current.seekTo(timestamp);
    }
    props.onChange(timestamp);
  };

  const minSliderValue = props.startTime || 0;
  const maxSliderValue = props.endTime || metadata.duration;

  return (
    <div>
      <div className="flex justify-center mb-2">
        <Typography variant="h4">{props.media.name}</Typography>
      </div>
      <div className="flex justify-center bg-black">
        <ReactPlayer
          onDuration={(duration) => setMetadata({ duration })}
          ref={playerRef}
          playing={false}
          seconds={sliderValue}
          playsinline
          height={300}
          url={videoSrc}
          style={{
            borderRadius: '6px',
            overflow: 'hidden',
          }}
        />
      </div>

      <div className="mt-5">
        <Typography variant="h5" className="font-semibold">
          Select a still to be used as the video thumbnail.
        </Typography>
        <Typography variant="body1">
          A good still stands out and draws viewers’ attention to the exercise.
        </Typography>
      </div>
      <VideoTimeLine
        startTime={props.startTime}
        duration={getDuration({ ...props, duration: metadata.duration })}
        height={props.height}
        width={props.width}
        src={videoSrc}
        ratio={props.ratio}
        onClick={thumbnailClick}
      />
      <Slider
        value={sliderValue}
        min={minSliderValue}
        max={maxSliderValue}
        step={0.01}
        onChange={handleSliderChange}
        onChangeCommitted={saveTimeStamp}
        sx={{ width: props.width }}
      />
    </div>
  );
}

export default MediaThumbnailSelect;
